import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Tooltip, IconButton, Tab, Tabs,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  LinearProgress,
} from '@mui/material';
import { AccountTree, Refresh, Warning, CheckCircle, Error as ErrIcon, People } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const SPAN_CFG = {
  under:   { label: 'Under-span (<3)',  color: '#e65100', bg: '#fff3e0', icon: <Warning   sx={{ fontSize: 14 }} /> },
  optimal: { label: 'Optimal (3–8)',    color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  over:    { label: 'Over-span (>8)',   color: '#b71c1c', bg: '#ffebee', icon: <ErrIcon   sx={{ fontSize: 14 }} /> },
};

function SpanChip({ span }: { span: 'under' | 'optimal' | 'over' }) {
  const c = SPAN_CFG[span];
  return <Chip size="small" icon={<Box sx={{ color: c.color, display: 'flex' }}>{c.icon}</Box>}
    label={c.label} sx={{ bgcolor: c.bg, color: c.color, fontSize: '0.6rem' }} />;
}

/** Simple bar proportional to max */
function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 10, bgcolor: '#f5f5f5', borderRadius: 5 }}>
        <Box sx={{ height: '100%', width: `${Math.min(100, max > 0 ? (value / max) * 100 : 0)}%`, bgcolor: color, borderRadius: 5 }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 20, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function SpanOfControl() {
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState(0);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch('/api/span-of-control')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary    = data?.summary ?? {};
  const managers   = data?.managers ?? [];
  const spanDist   = data?.spanDistribution ?? [];
  const deptSumm   = data?.deptSummary ?? [];
  const layers     = data?.layerAnalysis ?? [];
  const orgDepth   = data?.orgDepth ?? [];
  const icVsMgr    = Array.isArray(data?.icVsManager) ? data.icVsManager[0] : (data?.icVsManager ?? {});
  const orphans    = data?.orphans ?? [];

  const maxDist    = Math.max(...spanDist.map((d: any) => d.managers), 1);
  const maxLayer   = Math.max(...layers.map((l: any) => l.headcount), 1);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#37474f', width: 48, height: 48 }}><AccountTree /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Org Span of Control</Typography>
            <Typography variant="body2" color="text.secondary">
              Manager ratios, hierarchy depth, over/under-span detection
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {data && (summary.overSpan > 0 || summary.underSpan > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary.overSpan > 0 && <><strong>{summary.overSpan} manager{summary.overSpan > 1 ? 's' : ''} over-span</strong> (&gt;8 direct reports) &nbsp;</>}
          {summary.underSpan > 0 && <><strong>{summary.underSpan} manager{summary.underSpan > 1 ? 's' : ''} under-span</strong> (&lt;3 direct reports)</>}
          {' '}— review org structure for optimal effectiveness.
        </Alert>
      )}
      {orphans.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{orphans.length} employee{orphans.length > 1 ? 's have' : ' has'} no active manager assigned.</strong>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPI row */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Total Managers',    value: summary.totalManagers,    color: '#37474f' },
              { label: 'Avg Direct Reports', value: summary.avgDirectReports, color: '#1565c0' },
              { label: 'Max Direct Reports', value: summary.maxDirectReports, color: '#C08A2E' },
              { label: '✅ Optimal Span',   value: summary.optimalSpan,      color: '#2e7d32' },
              { label: '⚠️ Under-span',    value: summary.underSpan,        color: '#e65100' },
              { label: '❌ Over-span',     value: summary.overSpan,         color: '#b71c1c' },
            ].map(k => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
                  <Typography variant="h5" fontWeight={700} color={k.color}>{k.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* IC vs Manager ratio */}
          {icVsMgr?.total > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>👥 IC vs Manager Ratio</Typography>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#1565c0">{icVsMgr.ics}</Typography>
                  <Typography variant="caption" color="text.secondary">Individual Contributors</Typography>
                </Box>
                <Typography variant="h5" color="text.disabled">:</Typography>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#37474f">{icVsMgr.managers}</Typography>
                  <Typography variant="caption" color="text.secondary">Managers</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <LinearProgress variant="determinate"
                    value={icVsMgr.total > 0 ? (icVsMgr.ics / icVsMgr.total) * 100 : 0}
                    sx={{ height: 12, borderRadius: 6,
                      bgcolor: '#37474f30',
                      '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                    <Typography variant="caption" color="#1565c0">
                      ICs {icVsMgr.total > 0 ? Math.round((icVsMgr.ics / icVsMgr.total) * 100) : 0}%
                    </Typography>
                    <Typography variant="caption" color="#37474f">
                      Managers {icVsMgr.total > 0 ? Math.round((icVsMgr.managers / icVsMgr.total) * 100) : 0}%
                    </Typography>
                  </Box>
                </Box>
                <Chip label={`1 manager : ${icVsMgr.managers > 0 ? (icVsMgr.ics / icVsMgr.managers).toFixed(1) : '—'} ICs`}
                  sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }} />
              </Box>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label="All Managers" />
            <Tab label="Span Distribution" />
            <Tab label="By Department" />
            <Tab label="Org Depth" />
            {orphans.length > 0 && <Tab label={`Orphans (${orphans.length})`} />}
          </Tabs>

          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Manager</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Direct Reports</TableCell>
                    <TableCell align="right">Total Org Size</TableCell>
                    <TableCell align="right">Org Depth</TableCell>
                    <TableCell>Span</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {managers.map((m: any) => (
                    <TableRow key={m.managerId} hover
                      sx={{ bgcolor: m.span === 'over' ? '#fff5f5' : m.span === 'under' ? '#fffde7' : undefined }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: '#37474f' }}>
                            {m.managerName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{m.managerName}</Typography>
                            <Typography variant="caption" color="text.secondary">{m.position}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="body2">{m.department}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}
                          color={m.directReports > 8 ? '#b71c1c' : m.directReports < 3 ? '#e65100' : '#2e7d32'}>
                          {m.directReports}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={m.totalOrgSize}
                          sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip size="small"
                          label={`${m.orgDepth} level${m.orgDepth !== 1 ? 's' : ''}`}
                          sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell><SpanChip span={m.span} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Span Distribution</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Number of managers by direct-report count
                  </Typography>
                  {spanDist.map((s: any) => {
                    const isDanger = parseInt(s.directReports) > 8;
                    const isWarn   = parseInt(s.directReports) < 3;
                    const color    = isDanger ? '#b71c1c' : isWarn ? '#e65100' : '#2e7d32';
                    return (
                      <Box key={s.directReports} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                        <Box sx={{ minWidth: 80, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight={600} color={color}>
                            {s.directReports}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">direct report{s.directReports !== 1 ? 's' : ''}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Bar value={s.managers} max={maxDist} color={color} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {s.managers} mgr{s.managers !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      🟢 Optimal: 3–8 direct reports &nbsp;·&nbsp;
                      🟡 Under-span: &lt;3 &nbsp;·&nbsp;
                      🔴 Over-span: &gt;8
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Layer Analysis</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Headcount at each hierarchy level (Layer 1 = top of org)
                  </Typography>
                  {layers.map((l: any) => (
                    <Box key={l.layer} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                      <Box sx={{ minWidth: 64 }}>
                        <Chip size="small" label={`Layer ${l.layer}`}
                          sx={{ bgcolor: l.layer === 1 ? '#e3f2fd' : l.layer <= 3 ? '#e8f5e9' : '#f3e5f5',
                            color: l.layer === 1 ? '#1565c0' : l.layer <= 3 ? '#2e7d32' : '#6a1b9a',
                            fontSize: '0.6rem' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Bar value={l.headcount} max={maxLayer} color={l.layer <= 2 ? '#1565c0' : l.layer <= 4 ? '#2e7d32' : '#6a1b9a'} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{l.headcount} employees</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell align="right">Managers</TableCell>
                    <TableCell align="right">ICs</TableCell>
                    <TableCell align="right">Avg Span</TableCell>
                    <TableCell>IC:Manager Ratio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptSumm.map((d: any) => {
                    const icRatio = d.managers > 0 ? (d.ics / d.managers).toFixed(1) : '—';
                    return (
                      <TableRow key={d.department} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{d.department}</TableCell>
                        <TableCell align="right">{d.headcount}</TableCell>
                        <TableCell align="right">{d.managers}</TableCell>
                        <TableCell align="right">{d.ics}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${d.avgSpan ?? '—'}`}
                            sx={{
                              bgcolor: !d.avgSpan ? '#f5f5f5'
                                : d.avgSpan > 8 ? '#ffebee' : d.avgSpan < 3 ? '#fff3e0' : '#e8f5e9',
                              color: !d.avgSpan ? '#9e9e9e'
                                : d.avgSpan > 8 ? '#b71c1c' : d.avgSpan < 3 ? '#e65100' : '#2e7d32',
                              fontWeight: 700, fontSize: '0.65rem',
                            }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate"
                              value={d.headcount > 0 ? (d.ics / d.headcount) * 100 : 0}
                              sx={{ flex: 1, height: 6, borderRadius: 3,
                                bgcolor: '#37474f20',
                                '& .MuiLinearProgress-bar': { bgcolor: '#1565c0' } }} />
                            <Typography variant="caption" color="text.secondary">{icRatio}:1</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Root Manager</TableCell>
                    <TableCell align="right">Max Depth (levels)</TableCell>
                    <TableCell align="right">Total Headcount</TableCell>
                    <TableCell>Depth visualisation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgDepth.map((o: any) => (
                    <TableRow key={o.rootId} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{o.rootName}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={`${o.maxDepth} level${o.maxDepth !== 1 ? 's' : ''}`}
                          sx={{
                            bgcolor: o.maxDepth > 5 ? '#ffebee' : o.maxDepth > 3 ? '#fff3e0' : '#e8f5e9',
                            color: o.maxDepth > 5 ? '#b71c1c' : o.maxDepth > 3 ? '#e65100' : '#2e7d32',
                            fontWeight: 700, fontSize: '0.65rem',
                          }} />
                      </TableCell>
                      <TableCell align="right">{o.totalHeadcount}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          {Array.from({ length: Math.min(o.maxDepth, 10) }).map((_, i) => (
                            <Box key={i} sx={{
                              width: 16, height: 16, borderRadius: 1,
                              bgcolor: i < 2 ? '#1565c0' : i < 4 ? '#2e7d32' : i < 6 ? '#f57f17' : '#b71c1c',
                              opacity: 0.7 + (i * 0.03),
                            }} />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 4 && orphans.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Issue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orphans.map((o: any) => (
                    <TableRow key={o.id} hover sx={{ bgcolor: '#ffebee22' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{o.fullName}</TableCell>
                      <TableCell>{o.department}</TableCell>
                      <TableCell>
                        <Chip size="small" label={o.reason} color="error" sx={{ fontSize: '0.65rem' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
